import { BaseSetting, TemsWebSetting } from "@/Settings/SettingsManager"
import { TemsObject3D } from "@/CommonTypes"
import { MeshInfo, MeshGroup, MeshPointCloud, ResultThermoTextureInfo } from "@/TemsManagerWebInterface"
import TemsWebEvents from "@/TemsWebEvents"
import THREE from "three"
import ThreeScene from "./ThreeScene";
import _ from "lodash";
require("./OrbitControls");

export class CameraSettings extends BaseSetting {
    constructor(
        public position: THREE.Vector3,
        public target: THREE.Vector3) {
        super();
    }

    fromJson(obj: any) {
        return new CameraSettings(
            new THREE.Vector3(obj.position.x, obj.position.y, obj.position.z),
            new THREE.Vector3(obj.target.x, obj.target.y, obj.target.z)
        );
    }
}

export enum SurfaceMaterial {
    Surface,
    Wireframe,
    Points
}

export interface ThreeScenePlugin {
    unRegister(): void
}

export default class ThreeViewObj {
    protected cameraSettings: TemsWebSetting<CameraSettings>;
    public renderer: THREE.WebGLRenderer;
    public camera: THREE.PerspectiveCamera;
    private controls: THREE.OrbitControls;
    public $elementScenery: JQuery;
    private threeScene: ThreeScene = null;
    public rendererHeight: number;
    private hasPendingFrame = false;
    private scenePluginGenerator: (view: ThreeViewObj, scene: ThreeScene) => ThreeScenePlugin[];
    private currentPlugins: ThreeScenePlugin[] = [];
    private contextedRenderFrame = () => this.renderFrame();

    constructor(
        cameraSettings: TemsWebSetting<CameraSettings>,
        scenePluginGenerator: (view: ThreeViewObj, scene: ThreeScene) => ThreeScenePlugin[] = () => []
    ) {
        this.cameraSettings = cameraSettings;
        this.scenePluginGenerator = scenePluginGenerator;
    }

    public replaceScenePluginGenerator(generator: (view: ThreeViewObj, scene: ThreeScene) => ThreeScenePlugin[]): ThreeScenePlugin[] {
        this.scenePluginGenerator = generator;
        if (this.hasScene()) {
            _.each(this.currentPlugins, (plugin) => {
                plugin.unRegister();
            });
            this.currentPlugins = this.scenePluginGenerator(this, this.threeScene);
            this.renderFrame();
        } else {
            this.currentPlugins = [];
        }

        return this.currentPlugins;
    }

    public getControls(): THREE.OrbitControls {
        return this.controls;
    }

    public init(elementScenery: JQuery, rendererHeight: number) {
        if (!(<any>window).WebGLRenderingContext) {
            throw "WebGL is not supported!";
        }

        this.$elementScenery = elementScenery;
        this.rendererHeight = rendererHeight;

        var rendererWidth = this.$elementScenery.innerWidth();

        this.camera = new THREE.PerspectiveCamera(60, rendererWidth / this.rendererHeight, 0.5, 200);
        this.camera.position.copy(this.cameraSettings.Current.position);

        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setClearColor(0xf7f5f1);
        this.renderer.setSize(rendererWidth, this.rendererHeight);
        this.renderer.sortObjects = true;

        this.$elementScenery.append(this.renderer.domElement);
        $(this.renderer.domElement).addClass("threeView")

        this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
        this.controls.minPolarAngle = 0;
        this.controls.maxPolarAngle = Math.PI * 0.475;
        this.controls.addEventListener("change", () => this.renderFrame());
        $(window).resize(() => this.handleOnResize());

        this.handleOnResize();
        this.setCameraTarget(this.cameraSettings.Current.target);
        this.renderer.render(new THREE.Scene(), this.camera);
    }

    public renderFrame() {
        if (!this.hasPendingFrame && this.threeScene) {
            this.hasPendingFrame = true;

            // Because JS has no Multi-Threading outside webworkers, hasPendingFrame cannot change during 
            // rendering(renderFrame() cannot be called during rendering). 
            window.requestAnimationFrame(() => {
                /*

                // The following lines are kept for debugging purpose
                var findRecursive = (el, exp) => {
                    var results = (exp(el)) ? [el] : [];
                    if (el.children) {
                        for (var child of el.children) {
                            results = results.concat(findRecursive(child, exp));
                        }
                    }

                    return results;
                }
                var scene = this.threeScene.scene;
                var find = _.groupBy(findRecursive(scene, (e: THREE.Mesh) => e.material && (<any>e.material).color.getHex() == 12538686)
                    .map((e) => { return { obj: e, color: e.material.color, visible: e.visible } }),
                    (a: THREE.MeshPhongMaterial) => a.color.getHex()
                );
                */
                this.hasPendingFrame = false;
                this.controls.update();
                this.onBeforeRender();
                this.renderer.render(this.threeScene.scene, this.camera);
                this.$elementScenery.trigger("renderDone");
            });
        }
    }

    public setCameraTarget(target: THREE.Vector3): void {
        this.controls.target = target;
    }

    public setCameraPosition(position: THREE.Vector3): void {
        this.camera.position.copy(position);
    }

    public setClearColor(color, alpha): void {
        this.renderer.setClearColor(color, alpha);
    }

    public setScene(scene: ThreeScene) {
        if (this.threeScene) {
            this.threeScene.offMeshChange(this.contextedRenderFrame);
            $.each(this.currentPlugins, (i, value) => value.unRegister());
        }

        if (scene) {
            this.threeScene = scene;
            this.threeScene.onMeshChange(this.contextedRenderFrame);
            this.currentPlugins = this.scenePluginGenerator(this, this.threeScene);
            TemsWebEvents.publishEvent(TemsWebEvents.SetSceneFinished);
            this.renderFrame();
        } else {
            this.threeScene = null;
            this.currentPlugins = [];
            this.renderer.render(new THREE.Scene(), this.camera);
        }
    }

    public changeRenderingMode(newRenderMode: SurfaceMaterial) {
        if (this.hasScene()) {
            this.threeScene.changeRenderingMode(newRenderMode);
        }
    }
    public changeSpecificRenderingMode(meshId: string, newRenderMode: SurfaceMaterial) {
        if (this.hasScene()) {
            this.threeScene.changeSpecificRenderingMode(meshId, newRenderMode);
        }
    }

    public hasScene(): boolean {
        return this.threeScene !== null;
    }

    //
    // @deprecated Use a ThreeScenePlugin instead, because Scene might always be missing
    //
    public getScene(): ThreeScene {
        if (!this.hasScene()) throw "ThreeView doesn't contain a ThreeScene";
        return this.threeScene;
    }

    public getCameraParameter(): CameraSettings {
        return new CameraSettings(
            new THREE.Vector3().copy((<any>this.controls).getCameraPosition()),
            new THREE.Vector3().copy((<any>this.controls).getCameraTarget())
        );
    }

    // Start the animation loop. Call this only once!
    protected onBeforeRender() { }

    handleOnResize() {
        var rendererWidth = this.$elementScenery.innerWidth();

        this.camera.aspect = rendererWidth / this.rendererHeight;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(rendererWidth, this.rendererHeight);
        this.renderFrame();
    }

    public saveCameraConfigInCookie(wheel: boolean): void {
        let action = () => this.cameraSettings.updateSettings((cameraSettings) => {
            const cameraParameter = this.getCameraParameter();
            cameraSettings.position = cameraParameter.position;
            cameraSettings.target = cameraParameter.target;
        });

        if (wheel) {
            action()
        } else {
            $(document).one("mouseup", action);
        }
    }

    public get AzimuthInWorld(): number {
        return this.controls.getAzimuthalAngle();
    }

    public set AzimuthInWorld(newAzimuth: number) {
        let diffAzimuth = newAzimuth - this.AzimuthInWorld;
        (<any>this.controls).setDeltaTheta(diffAzimuth);
    }

    public azimuthCameraToTarget(target: THREE.Vector3): number {
        const camera = (<any>this.controls).getCameraPosition();
        const direction = target.clone().sub(camera);
        const theta = Math.atan2(direction.x, direction.z);
        return theta;
    }

    public resetCamera() {
        this.controls.reset();
    }
}
